'use client';

import type { FormattedMessageProps } from './FormattedMessage.types';
import { useContext, useState, useEffect, type FC } from 'react';
import styles from './FormattedMessage.module.css';
import { Text, Button, Flex, Icon, ButtonGroup, Select } from '@/_components';
import { FormattedQuestionnaires } from './_components/FormattedQuestionnaires';
import { AdminAIContext } from '@/_contexts/admin/AdminAIProvider/AdminAIProvider';
import { renderMarkdown } from '@/_utils/markdown';
import { extractQuestionnaireData } from '@/_utils/ai';
import { useParams } from 'next/navigation';
import { getClasses } from '@/_utils/classnames';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { generateQuestionnaireReport } from '@/_api/admin-reports';
import { API_URL } from '@/_libs/constants';

export const FormattedMessage: FC<FormattedMessageProps> = ({
  content,
  questionnaireIds,
  questionIds,
  questionnairePairs,
  questionnaireMetadata,
  graphql_data,
  filters: backendFilters,
}) => {
  const { openPreviewQuestionnaire, loadingQuestionnaireId } =
    useContext(AdminAIContext);
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);

  const [areSourcesExpanded, setAreSourcesExpanded] = useState(false);
  const [areQuestionnairesExpanded, setAreQuestionnairesExpanded] =
    useState(false);
  const [isLegacySourceExpanded, setIsLegacySourceExpanded] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  // Unified keyword search state
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<{
    type: 'questions' | 'clients';
    data:
      | Array<{
          id: string;
          text: string;
          answer: string;
          questionnaire: {
            id: string;
            name: string;
            taxYear: number;
            company: {
              id: string;
              name: string;
            };
          };
        }>
      | Array<{
          companyId: number;
          companyName: string;
          questionnaireId: number;
          questionIds: number[];
          questions: string[];
          answers: string[];
        }>;
  } | null>(null);

  // Local filter state for UI updates
  const [localFilters, setLocalFilters] =
    useState<typeof backendFilters>(backendFilters);

  const { questionnaireId: questionnaireIdParam } = useParams();

  // Update local filters when backend filters change
  useEffect(() => {
    if (backendFilters) {
      setLocalFilters(backendFilters);
    }
  }, [backendFilters]);

  // Automatically populate keywords from textSearch and answerKeywords filters
  useEffect(() => {
    const textSearchTerms = backendFilters?.textSearch || [];
    const answerKeywordTerms = backendFilters?.answerKeywords || [];
    const allTerms = [...textSearchTerms, ...answerKeywordTerms];

    if (allTerms.length > 0) {
      // Process all terms and add unique ones to keywords in a single update
      const newKeywords = allTerms
        .filter(
          (term): term is string =>
            typeof term === 'string' && term.trim().length > 0,
        )
        .map((term) => term.trim());

      // Add new keywords that don't already exist
      setKeywords((prev) => {
        const uniqueNewKeywords = newKeywords.filter(
          (keyword) => !prev.includes(keyword),
        );
        if (uniqueNewKeywords.length > 0) {
          return [...prev, ...uniqueNewKeywords];
        }
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendFilters?.textSearch, backendFilters?.answerKeywords]);

  // Handler for generating questionnaire report
  const handleGenerateReport = async () => {
    try {
      setIsGeneratingReport(true);

      // Extract the most common tax year from metadata, or use current year
      let year = new Date().getFullYear();
      if (questionnaireMetadata && questionnaireMetadata.length > 0) {
        const years = questionnaireMetadata
          .map((meta) => meta.tax_year)
          .filter((y): y is number => typeof y === 'number');
        if (years.length > 0) {
          // Get the most common year
          const yearCounts = years.reduce(
            (acc, y) => {
              acc[y] = (acc[y] || 0) + 1;
              return acc;
            },
            {} as Record<number, number>,
          );
          year = Number(
            Object.keys(yearCounts).reduce((a, b) =>
              yearCounts[Number(a)] > yearCounts[Number(b)] ? a : b,
            ),
          );
        }
      }

      const response = await generateQuestionnaireReport({ token, year });

      if (response.error) {
        addToast('error', 'Failed to generate Questionnaires report');
      } else {
        addToast(
          'success',
          'Report is being generated. You will be notified when the report is done.',
        );
      }
    } catch (error) {
      addToast('error', 'Failed to generate Questionnaires report');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  if (!content) {
    return <div className={styles.formattedMessage}>No content to display</div>;
  }

  const containsQuestionnaire: boolean =
    content.includes('Questionnaire:') || content.includes('questionnaire:');

  const formatContent = () => {
    // Always strip any streaming cursor span before rendering
    const cleanContent = (content || '').replace(
      /<span class="[^"]*streamingCursor[^"]*">_<\/span>/g,
      '',
    );

    const markdownHtml = renderMarkdown(cleanContent);

    return <div dangerouslySetInnerHTML={{ __html: markdownHtml }} />;
  };

  // Extract questionnaire and question data from graphql_data if available
  const {
    questionnaireIds: extractedQuestionnaireIds,
    questionIds: extractedQuestionIds,
    questionnairePairs: extractedQuestionnairePairs,
  } = extractQuestionnaireData(graphql_data || []);

  // Use extracted data if available, otherwise fall back to existing data
  const finalQuestionnaireIds =
    extractedQuestionnaireIds.length > 0
      ? extractedQuestionnaireIds
      : questionnaireIds;
  const finalQuestionIds =
    extractedQuestionIds.length > 0 ? extractedQuestionIds : questionIds;
  const finalQuestionnairePairs =
    extractedQuestionnairePairs.length > 0
      ? extractedQuestionnairePairs
      : questionnairePairs;

  // Helper function to render sources container
  const renderSourcesContainer = (
    isExpanded: boolean,
    toggleExpanded: () => void,
    count: number,
    children: React.ReactNode,
  ) => (
    <Flex isColumn={true} paddingX={6} className={styles.sourcesContainer}>
      <ButtonGroup>
        <Button
          intent="ghost"
          iconLeft={isExpanded ? 'caret-down' : 'caret-right'}
          value={`View sources (${count})`}
          onClick={toggleExpanded}
          className={styles.sourcesToggleButton}
        />
      </ButtonGroup>
      {isExpanded && (
        <Flex isColumn gap={2}>
          {children}
        </Flex>
      )}
    </Flex>
  );

  // Helper function to get questionnaire display text
  const getQuestionnaireDisplayText = (
    questionnaireName?: string,
    companyName?: string,
    taxYear?: number,
  ) => {
    return `Questionnaire${questionnaireName ? `: ${questionnaireName}` : ''}${companyName ? ` — ${companyName}` : ''}${taxYear ? ` — ${taxYear}` : ''}`;
  };

  // Helper to format filter value for display
  const formatFilterValue = (key: string, values: any[]): string => {
    if (!values || values.length === 0) return '[All]';

    if (key === 'completed' || key === 'currentStep') {
      if (values.length === 1) {
        return values[0] === true || values[0] === 'complete' ? 'Yes' : 'No';
      }
    }

    return values.length === 1
      ? String(values[0])
      : values.map(String).join(', ');
  };

  // Helper function to determine filters used based on backend filters or fallback to metadata
  const getFiltersUsed = () => {
    // If we have local filters, use those (more accurate)
    if (localFilters && Object.keys(localFilters).length > 0) {
      const filters: Record<string, string> = {};

      Object.keys(localFilters).forEach((key) => {
        const values = localFilters[key as keyof typeof localFilters];
        if (values) {
          filters[key] = formatFilterValue(key, values as any[]);
        }
      });

      return filters;
    }

    // Fallback: infer from metadata (less accurate)
    if (!questionnaireMetadata || questionnaireMetadata.length === 0) {
      return null;
    }

    // Get unique tax years
    const years = questionnaireMetadata
      .map((meta) => meta.tax_year)
      .filter((y): y is number => typeof y === 'number');
    const uniqueYears = Array.from(new Set(years));
    const taxYearFilter =
      uniqueYears.length === 1 ? String(uniqueYears[0]) : '[All]';

    // Get unique questionnaire names
    const names = questionnaireMetadata
      .map((meta) => meta.name)
      .filter((n): n is string => typeof n === 'string' && n.length > 0);
    const uniqueNames = Array.from(new Set(names));
    const questionnaireNameFilter =
      uniqueNames.length === 1 ? uniqueNames[0] : '[All]';

    // Try to determine completion status from content
    let completedFilter = '[All]';
    const contentLower = content.toLowerCase();
    if (
      contentLower.includes('incomplete') ||
      contentLower.includes('uncompleted') ||
      contentLower.includes('not completed')
    ) {
      completedFilter = 'No';
    } else if (
      contentLower.includes('completed') &&
      !contentLower.includes('incomplete')
    ) {
      completedFilter = 'Yes';
    }

    return {
      taxYear: taxYearFilter,
      questionnaireName: questionnaireNameFilter,
      completed: completedFilter,
    };
  };

  // Get all available years and questionnaire names for dropdowns
  const getAvailableOptions = () => {
    // Always provide a reasonable range of tax years (last 5 years + current + next)
    const currentYear = new Date().getFullYear();
    const defaultYears = Array.from(
      { length: 7 },
      (_, i) => currentYear - 5 + i,
    ).reverse(); // [currentYear+1, currentYear, ..., currentYear-5]

    let years = defaultYears;
    let names: string[] = [];

    // If we have metadata, extract years and names from it
    if (questionnaireMetadata && questionnaireMetadata.length > 0) {
      const metadataYears = questionnaireMetadata
        .map((meta) => meta.tax_year)
        .filter((y): y is number => typeof y === 'number');
      const uniqueMetadataYears = Array.from(new Set(metadataYears)).sort(
        (a, b) => b - a,
      );

      // Merge with default years and deduplicate
      if (uniqueMetadataYears.length > 0) {
        const allYears = Array.from(
          new Set([...uniqueMetadataYears, ...defaultYears]),
        ).sort((a, b) => b - a);
        years = allYears;
      }

      const metadataNames = questionnaireMetadata
        .map((meta) => meta.name)
        .filter((n): n is string => typeof n === 'string' && n.length > 0);
      names = Array.from(new Set(metadataNames)).sort();
    }

    // Also extract years from local filters if available
    if (localFilters?.taxYear && localFilters.taxYear.length > 0) {
      const filterYears = localFilters.taxYear.filter(
        (y): y is number => typeof y === 'number',
      );
      if (filterYears.length > 0) {
        const allYears = Array.from(new Set([...filterYears, ...years])).sort(
          (a, b) => b - a,
        );
        years = allYears;
      }
    }

    // Also extract company names from local filters if available
    if (localFilters?.companyName && localFilters.companyName.length > 0) {
      const filterCompanyNames = localFilters.companyName.filter(
        (n): n is string => typeof n === 'string' && n.length > 0,
      );
      if (filterCompanyNames.length > 0) {
        names = Array.from(new Set([...names, ...filterCompanyNames])).sort();
      }
    }

    return { years, names };
  };

  const handleFilterChange = async (filterType: string, value: string) => {
    // Parse value appropriately based on filter type
    let filterValue: any = value === '[All]' ? [] : [value];

    // Convert taxYear to number
    if (filterType === 'taxYear' && value !== '[All]') {
      const yearNum = parseInt(value, 10);
      if (!isNaN(yearNum)) {
        filterValue = [yearNum];
      }
    }

    // Update local filters for immediate UI feedback
    const updatedFilters = {
      ...localFilters,
      [filterType]: filterValue,
    };
    setLocalFilters(updatedFilters);

    // If we have keywords, automatically requery with the new filters
    if (keywords.length > 0) {
      await performSearch(keywords, updatedFilters);
    }
  };

  // Keyword management handlers
  const handleAddKeyword = (keyword: string) => {
    const trimmed = keyword.trim();
    if (trimmed && !keywords.includes(trimmed)) {
      setKeywords([...keywords, trimmed]);
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setKeywords(keywords.filter((k) => k !== keyword));
  };

  const handleKeywordKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword(keywordInput);
    }
  };

  // Extracted search logic to be reused
  const performSearch = async (
    searchKeywords: string[],
    filters: typeof backendFilters,
  ) => {
    if (!token) {
      addToast('error', 'Authentication required');
      return;
    }

    // Show loading state
    setIsFilterLoading(true);

    try {
      const searchText = searchKeywords.join(' ');

      // Detect which type of search to perform
      // If answerKeywords or questionKeywords are present, use searchClientsByAnswer
      // Otherwise, use searchQuestionnaireQuestions
      const hasAnswerKeywords =
        filters?.answerKeywords && filters.answerKeywords.length > 0;
      const hasQuestionKeywords =
        filters?.questionKeywords && filters.questionKeywords.length > 0;
      const useClientSearch = hasAnswerKeywords || hasQuestionKeywords;

      if (useClientSearch) {
        // Build GraphQL query to search clients by answer
        const graphqlQuery = {
          query: `
            query SearchClientsByAnswer(
              $taxYear: Int!,
              $answerKeywords: [String!]!,
              $questionKeywords: [String!]
            ) {
              searchClientsByAnswer(
                taxYear: $taxYear,
                answerKeywords: $answerKeywords,
                questionKeywords: $questionKeywords
              ) {
                edges {
                  node {
                    companyId
                    companyName
                    questionnaireId
                    questionIds
                    questions
                    answers
                  }
                }
              }
            }
          `,
          variables: {
            taxYear: filters?.taxYear?.[0] || new Date().getFullYear(),
            answerKeywords: hasAnswerKeywords
              ? filters.answerKeywords
              : searchKeywords,
            ...(hasQuestionKeywords && {
              questionKeywords: filters.questionKeywords,
            }),
          },
        };

        // Call the GraphQL endpoint directly
        const response = await fetch(`${API_URL}/graphql`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: token } : {}),
          },
          body: JSON.stringify(graphqlQuery),
        });

        if (!response.ok) {
          throw new Error(`GraphQL request failed: ${response.status}`);
        }

        const result = await response.json();

        if (result.errors) {
          console.error('GraphQL errors:', result.errors);
          throw new Error(
            result.errors[0]?.message || 'GraphQL query returned errors',
          );
        }

        const edges = result.data?.searchClientsByAnswer?.edges || [];
        const clients = edges.map((edge: any) => ({
          companyId: edge.node.companyId,
          companyName: edge.node.companyName,
          questionnaireId: edge.node.questionnaireId,
          questionIds: edge.node.questionIds,
          questions: edge.node.questions,
          answers: edge.node.answers,
        }));

        if (clients.length > 0) {
          setSearchResults({ type: 'clients', data: clients });

          const totalQuestions = clients.reduce(
            (sum: number, client: any) =>
              sum + (client.questionIds?.length || 0),
            0,
          );

          addToast(
            'success',
            `Found ${clients.length} client${clients.length !== 1 ? 's' : ''} with ${totalQuestions} matching answer${totalQuestions !== 1 ? 's' : ''}`,
          );
        } else {
          setSearchResults(null);
          addToast('info', 'No matching clients found');
        }
      } else {
        // Build GraphQL query to search questionnaire questions
        const graphqlQuery = {
          query: `
            query SearchQuestionnaireQuestions(
              $textSearch: String,
              $companyName: String,
              $taxYear: Int
            ) {
              searchQuestionnaireQuestions(
                textSearch: $textSearch,
                companyName: $companyName,
                taxYear: $taxYear
              ) {
                edges {
                  node {
                    id
                    text
                    answer
                    section {
                      questionnaire {
                        id
                        name
                        taxYear
                        company {
                          id
                          name
                        }
                      }
                    }
                  }
                }
              }
            }
          `,
          variables: {
            textSearch: searchText,
            ...(filters?.companyName &&
              filters.companyName.length > 0 && {
                companyName: filters.companyName[0],
              }),
            ...(filters?.taxYear &&
              filters.taxYear.length > 0 && {
                taxYear: filters.taxYear[0],
              }),
          },
        };

        // Call the GraphQL endpoint directly
        const response = await fetch(`${API_URL}/graphql`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: token } : {}),
          },
          body: JSON.stringify(graphqlQuery),
        });

        if (!response.ok) {
          throw new Error(`GraphQL request failed: ${response.status}`);
        }

        const result = await response.json();

        if (result.errors) {
          console.error('GraphQL errors:', result.errors);
          throw new Error(
            result.errors[0]?.message || 'GraphQL query returned errors',
          );
        }

        const edges = result.data?.searchQuestionnaireQuestions?.edges || [];
        const questions = edges.map((edge: any) => ({
          id: edge.node.id,
          text: edge.node.text,
          answer: edge.node.answer,
          questionnaire: {
            id: edge.node.section?.questionnaire?.id || '',
            name: edge.node.section?.questionnaire?.name || '',
            taxYear: edge.node.section?.questionnaire?.taxYear || 0,
            company: {
              id: edge.node.section?.questionnaire?.company?.id || '',
              name: edge.node.section?.questionnaire?.company?.name || '',
            },
          },
        }));

        if (questions.length > 0) {
          // Get unique questionnaires
          const uniqueQuestionnaireIds = [
            ...new Set(
              questions
                .map((q: (typeof questions)[0]) => q.questionnaire.id)
                .filter(Boolean),
            ),
          ];

          setSearchResults({ type: 'questions', data: questions });

          addToast(
            'success',
            `Found ${questions.length} matching answer${questions.length !== 1 ? 's' : ''} in ${uniqueQuestionnaireIds.length} questionnaire${uniqueQuestionnaireIds.length !== 1 ? 's' : ''}`,
          );
        } else {
          setSearchResults(null);
          addToast('info', 'No matching answers found');
        }
      }
    } catch (error) {
      console.error('Error searching:', error);
      addToast(
        'error',
        `Failed to search: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    } finally {
      setIsFilterLoading(false);
    }
  };

  const handleSearchWithKeywords = async () => {
    if (keywords.length === 0) {
      addToast('info', 'Please add at least one keyword to search');
      return;
    }

    await performSearch(keywords, localFilters);
  };

  // If the content looks like a questionnaire list but we do NOT have structured ids, fallback to text formatter
  if (
    containsQuestionnaire &&
    (!finalQuestionnaireIds || finalQuestionnaireIds.length === 0)
  ) {
    return <FormattedQuestionnaires content={content} />;
  }

  const hasQuestionnaireLinks: boolean = Boolean(
    (!finalQuestionnairePairs || finalQuestionnairePairs.length === 0) &&
      finalQuestionnaireIds &&
      finalQuestionnaireIds.length > 0,
  );

  const hasLegacyQuestionnaireLinks: boolean = Boolean(
    !finalQuestionnairePairs &&
      finalQuestionnaireIds &&
      finalQuestionnaireIds.length > 0 &&
      finalQuestionIds &&
      finalQuestionIds.length > 0,
  );

  const formattedContent = formatContent();
  const filters = getFiltersUsed();
  const { years } = getAvailableOptions();

  // Show filters whenever they exist (regardless of whether there are questionnaire links)
  const hasFilters = Boolean(filters);

  // Show download button only when there are questionnaire links
  const hasQuestionnaireData =
    hasQuestionnaireLinks ||
    (finalQuestionnairePairs && finalQuestionnairePairs.length > 0);

  // Handler for clearing all filters
  const handleClearAll = () => {
    setKeywords([]);
    setKeywordInput('');
    setSearchResults(null);
    setLocalFilters(backendFilters);
  };

  return (
    <Flex className={styles.formattedMessage} isColumn={true} gap={8}>
      {/* Simplified Search section */}
      {hasFilters && (
        <Flex
          isColumn={true}
          gap={6}
          padding={6}
          className={styles.refineSearchContainer}
        >
          {/* Header */}
          <Flex isBetween={true} isYCentered={true}>
            <Text fontWeight="semi-bold">Refine Search</Text>
            <Button
              intent="ghost"
              size="small"
              value="Clear All"
              onClick={handleClearAll}
              className={styles.clearButton}
            />
          </Flex>

          {/* Tax Year Filter */}
          {filters && filters.taxYear && (
            <Flex isColumn={true} gap={4}>
              <Text type="tiny" fontWeight="medium" color="secondary">
                Tax Year
              </Text>
              <Select
                label="Tax Year"
                isLabelHidden={true}
                selectedValue={filters.taxYear || '[All]'}
                onChange={(e) => handleFilterChange('taxYear', e.target.value)}
                items={[
                  { name: 'All', value: '[All]' },
                  ...years.map((year) => ({
                    name: String(year),
                    value: String(year),
                  })),
                ]}
              />
            </Flex>
          )}

          {/* Keyword Search */}
          <Flex isColumn={true} gap={4}>
            <Text type="tiny" fontWeight="medium" color="secondary">
              Search Keywords
            </Text>

            {keywords.length > 0 && (
              <Flex gap={2} className={styles.keywordChipsContainer}>
                {keywords.map((keyword) => (
                  <Flex
                    key={keyword}
                    gap={2}
                    isYCentered={true}
                    className={styles.keywordChip}
                  >
                    <Text type="tiny" color="currentcolor">
                      {keyword}
                    </Text>
                    <button
                      onClick={() => handleRemoveKeyword(keyword)}
                      className={styles.keywordChipRemove}
                      aria-label={`Remove ${keyword}`}
                    >
                      <Icon name="x" size={12} />
                    </button>
                  </Flex>
                ))}
              </Flex>
            )}

            <input
              type="text"
              className={styles.keywordInput}
              placeholder="Add keywords (press Enter) — e.g., PPP, estimated tax"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyPress={handleKeywordKeyPress}
            />

            <Button
              intent="primary"
              size="small"
              iconLeft="search"
              value="Search with Filters"
              onClick={handleSearchWithKeywords}
              isDisabled={isFilterLoading}
              className={styles.searchButton}
            />
          </Flex>

          {/* Search Results */}
          {searchResults !== null && (
            <Flex
              isColumn={true}
              gap={4}
              className={styles.searchResultsSection}
            >
              <Text type="small" fontWeight="semi-bold">
                Search Results (
                {Array.isArray(searchResults.data)
                  ? searchResults.data.length
                  : 0}
                )
              </Text>

              {Array.isArray(searchResults.data) &&
              searchResults.data.length === 0 ? (
                <Flex
                  isColumn={true}
                  isCentered={true}
                  padding={8}
                  className={styles.emptyState}
                >
                  <Text type="small" color="secondary">
                    No matching results found
                  </Text>
                </Flex>
              ) : searchResults.type === 'clients' ? (
                <Flex
                  isColumn={true}
                  gap={4}
                  className={styles.searchResultsContainer}
                >
                  {(
                    searchResults.data as Array<{
                      companyId: number;
                      companyName: string;
                      questionnaireId: number;
                      questionIds: number[];
                      questions: string[];
                      answers: string[];
                    }>
                  ).map((client, idx) => (
                    <Flex
                      key={`${client.companyId}-${idx}`}
                      isColumn={true}
                      gap={2}
                      padding={4}
                      className={styles.searchResultCard}
                      onClick={() => {
                        if (client.questionnaireId) {
                          openPreviewQuestionnaire(client.questionnaireId);
                        }
                      }}
                    >
                      {/* Client Info */}
                      <Flex gap={2} isYCentered={true}>
                        <Text type="small" fontWeight="semi-bold">
                          {client.companyName}
                        </Text>
                      </Flex>

                      {/* Questions and Answers */}
                      {client.questions.map((question, qIdx) => (
                        <Flex key={qIdx} isColumn={true} gap={1}>
                          <Text type="tiny" color="secondary">
                            Q{qIdx + 1}: {question}
                          </Text>
                          {client.answers[qIdx] && (
                            <Text type="tiny" fontWeight="medium">
                              A: {client.answers[qIdx]}
                            </Text>
                          )}
                        </Flex>
                      ))}
                    </Flex>
                  ))}
                </Flex>
              ) : (
                <Flex
                  isColumn={true}
                  gap={4}
                  className={styles.searchResultsContainer}
                >
                  {(
                    searchResults.data as Array<{
                      id: string;
                      text: string;
                      answer: string;
                      questionnaire: {
                        id: string;
                        name: string;
                        taxYear: number;
                        company: {
                          id: string;
                          name: string;
                        };
                      };
                    }>
                  ).map((result) => (
                    <Flex
                      key={result.id}
                      isColumn={true}
                      gap={2}
                      padding={4}
                      className={styles.searchResultCard}
                      onClick={() => {
                        const questionnaireId = parseInt(
                          result.questionnaire.id,
                        );
                        if (questionnaireId) {
                          openPreviewQuestionnaire(questionnaireId);
                        }
                      }}
                    >
                      {/* Company and Questionnaire Info */}
                      <Flex gap={2} isYCentered={true}>
                        <Text type="small" fontWeight="semi-bold">
                          {result.questionnaire.company.name} -{' '}
                          {result.questionnaire.name}
                        </Text>
                      </Flex>
                      <Text type="tiny" color="secondary">
                        Tax Year {result.questionnaire.taxYear}
                      </Text>

                      {/* Question */}
                      <Flex isColumn={true} gap={1}>
                        <Text type="tiny" color="secondary">
                          Question:
                        </Text>
                        <Text type="tiny">{result.text}</Text>
                      </Flex>

                      {/* Answer */}
                      {result.answer && (
                        <Flex isColumn={true} gap={1}>
                          <Text type="tiny" color="secondary">
                            Answer:
                          </Text>
                          <Text type="tiny" fontWeight="medium">
                            {result.answer}
                          </Text>
                        </Flex>
                      )}
                    </Flex>
                  ))}
                </Flex>
              )}
            </Flex>
          )}

          {/* Loading overlay */}
          {isFilterLoading && (
            <div className={styles.loadingOverlay}>
              <div className={styles.spinner} />
            </div>
          )}
        </Flex>
      )}

      {/* Results count display */}
      {hasQuestionnaireData && finalQuestionnaireIds && (
        <Flex paddingX={6} paddingY={2}>
          <div className={styles.resultsCount}>
            Found <strong>{finalQuestionnaireIds.length}</strong>{' '}
            {finalQuestionnaireIds.length === 1
              ? 'questionnaire'
              : 'questionnaires'}
          </div>
        </Flex>
      )}

      <Text type="div" className={styles.formattedMessage__md}>
        {formattedContent}
      </Text>

      {/* Show questionnaire links if present (hide when search results are displayed) */}
      {hasQuestionnaireLinks &&
        !searchResults &&
        renderSourcesContainer(
          areQuestionnairesExpanded,
          () => setAreQuestionnairesExpanded(!areQuestionnairesExpanded),
          finalQuestionnaireIds?.length || 0,
          finalQuestionnaireIds?.map((questionnaireId, idx) => {
            const questionId =
              finalQuestionIds && finalQuestionIds[idx]
                ? finalQuestionIds[idx]
                : undefined;

            const metadata = questionnaireMetadata?.find(
              (meta) => String(meta.id) === String(questionnaireId),
            );

            const isActive: boolean =
              questionnaireIdParam === String(questionnaireId);

            const formattedMessageClasses: string = getClasses(
              styles.questionnaireItem,
              {
                [styles.questionnaireItemActive]: isActive,
              },
            );

            const formattedMessageIconClasses: string = getClasses(
              styles.formattedMessage__icon,
              {
                [styles.formattedMessage__iconActive]: isActive,
              },
            );

            const onOpenQuestionnaire = () =>
              questionnaireId &&
              openPreviewQuestionnaire(
                questionnaireId,
                typeof questionId === 'number' ? questionId : undefined,
              );

            return (
              <Flex
                padding={2}
                isYCentered={true}
                gap={4}
                key={questionnaireId || idx}
                onClick={onOpenQuestionnaire}
                className={formattedMessageClasses}
              >
                <Flex className={formattedMessageIconClasses} isCentered={true}>
                  <Icon size={16} name={isActive ? 'eye' : 'cursor-click'} />
                </Flex>
                <Text type="small" fontWeight="medium" color="currentcolor">
                  {getQuestionnaireDisplayText(
                    metadata?.name,
                    metadata?.company_name,
                    metadata?.tax_year,
                  )}
                </Text>
              </Flex>
            );
          }),
        )}

      {/* Add source link if questionnaire pairs exist (hide when search results are displayed) */}
      {finalQuestionnairePairs &&
        finalQuestionnairePairs.length > 0 &&
        !searchResults &&
        renderSourcesContainer(
          areSourcesExpanded,
          () => setAreSourcesExpanded(!areSourcesExpanded),
          finalQuestionnairePairs.length,
          finalQuestionnairePairs.map((pair, idx) => {
            // Get metadata from backend only - no fallback to context
            const metadata = questionnaireMetadata?.find(
              (meta) => String(meta.id) === String(pair.questionnaire_id),
            );

            return (
              <div
                key={`${pair.questionnaire_id}-${pair.question_id}-${idx}`}
                className={styles.sourceButtonWithText}
              >
                <a
                  href="#"
                  className={styles.sourceButton}
                  onClick={(e) => {
                    e.preventDefault();
                    openPreviewQuestionnaire(
                      pair.questionnaire_id,
                      pair.question_id,
                    );
                  }}
                >
                  {metadata && (
                    <>
                      {metadata.company_name || ''}
                      {metadata.name ? ` - ${metadata.name}` : ''}
                      {metadata.tax_year ? ` — ${metadata.tax_year}` : ''}
                    </>
                  )}
                  {(pair.question_text || pair.text) && (
                    <>
                      <br />
                      <span
                        className={styles.responseText}
                        dangerouslySetInnerHTML={{
                          __html: renderMarkdown(
                            pair.question_text || pair.text || '',
                          ),
                        }}
                      />
                    </>
                  )}
                </a>
              </div>
            );
          }),
        )}

      {/* Legacy: Add source link if questionnaireIds exist but it's not a list format (hide when search results are displayed) */}
      {hasLegacyQuestionnaireLinks &&
        !searchResults &&
        renderSourcesContainer(
          isLegacySourceExpanded,
          () => setIsLegacySourceExpanded(!isLegacySourceExpanded),
          1,
          <Button
            intent="secondary"
            size="small"
            isLoading={finalQuestionnaireIds?.[0] === loadingQuestionnaireId}
            iconLeft="link"
            value="View Source"
            onClick={() =>
              openPreviewQuestionnaire(
                finalQuestionnaireIds?.[0]!,
                finalQuestionIds?.[0],
              )
            }
          />,
        )}

      {/* Download Report section when questionnaire data is present (hide when search results are displayed) */}
      {hasQuestionnaireData && !searchResults && (
        <Flex paddingX={6} paddingY={4}>
          <Button
            intent="secondary"
            size="small"
            isLoading={isGeneratingReport}
            iconLeft="download"
            value="Download Report"
            onClick={handleGenerateReport}
          />
        </Flex>
      )}
    </Flex>
  );
};
